import { LoadingState, Button, PageHeader, StatusBadge } from '../components/ui/Primitives';
import React, { useEffect, useState } from 'react';
import { trackMetaEvent } from '../utils/metaPixel';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Plans.css';
import { logEvent } from '../utils/logger';

const dollars = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

function Plans({ user }) {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');

  function loadPlans() {
    setError('');
    axios.get(`${process.env.REACT_APP_BACKEND_HOST}/billing/plans`)
      .then(response => setCatalog(response.data))
      .catch(() => setError('Pricing is temporarily unavailable. Please try again.'));
  }

  function selectPlan(plan) {
    trackMetaEvent('PlanSelected', { plan }, true);
    logEvent({ event: { label: 'Click', data: { value: `Plan Selected: ${plan}` } } });
    navigate('/signup', { state: { plan, pricingVersion: catalog.version } });
  }

  useEffect(() => { loadPlans(); }, []);
  useEffect(() => {
    logEvent({ event: { label: 'PageView', data: { page: 'Pricing', user_id: user?.user_id || null } } });
  }, [user?.user_id]);

  const monthly = catalog?.version === '2026-09';
  return (
    <div className="plans-page">
      <div className="page-content plans-content">
        <PageHeader eyebrow="Monthly pricing" title="Choose the coverage your business needs" description={monthly ? 'Try Sayable for 14 days with 30 AI minutes. Each plan includes one AI receptionist configuration. No credit card needed to get started.' : 'Start with a 14-day trial. Choose monthly coverage for your business.'} />
        {error && <p role="alert">{error} <Button onClick={loadPlans}>Try again</Button></p>}
        {!catalog && !error && <LoadingState label="Loading monthly plans…" />}
        <div className="plans-grid">
          {catalog?.plans.map(plan => {
            const features = ['24/7 AI call answering', 'Message taking and appointment scheduling',
              ...(plan.id === 'solo' ? ['One organization member'] : ['Call routing and transfers', `Up to ${plan.members} organization members`]),
              ...(plan.crm_workflows ? [`Up to ${plan.crm_workflows} active CRM workflows`] : []),
              `${dollars(plan.overage_cents_per_minute)} per additional AI minute`];
            return (
              <article className={`plan-column${plan.id === 'business' ? ' featured' : ''}`} key={plan.id}>
                <div className="plan-heading">{plan.id === 'business' && <StatusBadge status="info" dot={false}>For teams</StatusBadge>}<h2>{plan.name}</h2><p>{plan.id === 'solo' ? 'For independent professionals.' : plan.id === 'business' ? 'Call coverage and routing for your team.' : 'More included minutes for a busy operation.'}</p></div>
                <div className="plan-price"><strong>{dollars(plan.monthly_cents)}</strong><span>USD / month</span></div>
                <span className="plan-minutes">{plan.included_minutes} AI minutes per month</span>
                <ul>{features.map(feature => <li key={feature}><span aria-hidden="true">✓</span>{feature}</li>)}</ul>
                <Button className="plan-button" variant={plan.id === 'business' ? 'primary' : 'secondary'} onClick={() => selectPlan(plan.id)}>Start 14-day trial</Button>
              </article>
            );
          })}
        </div>
        {catalog && <>
          <div className="plan-enterprise"><div><h2>Enterprise</h2><p>{monthly ? 'Custom plans starting at a $499 USD monthly commitment. Locations, usage, integrations, and support are agreed in your quote.' : 'Custom capacity, additional capabilities, and support for larger organizations.'}</p></div><a href="mailto:support@sayable.ai?subject=Sayable%20Enterprise">Contact sales</a></div>
          {monthly && <p className="plan-disclosure">Each price covers one AI receptionist configuration, plus applicable tax. Paid service includes the monthly allowance shown above; the trial includes 30 minutes total and one active AI call at a time. AI time is measured in seconds, including browser test calls. Human conversation after a transfer is excluded. Unused minutes do not roll over. Add a payment method to continue automatically after the trial. Without a payment method, the trial subscription ends automatically. You can start a new paid subscription later. Cancel before renewal in your billing portal.</p>}
        </>}
      </div>
    </div>
  );
}

export default Plans;
